import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  CustomerReservationPart,
  CustomerReservationTimeScope
} from '../../../../../../../models/customer';
import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import {
  capitalizeFirstLetter,
  dateStringFromISO8601, getEventDetailsLink,
  timeStringFromISO8601TimeOnly
} from '../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { LocalStorage } from 'angular-web-storage';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ListResponse } from '../../../../../../../models/list-response';
import { DetailsModalDeepLink } from '../../../../../details-modal-deep-link';
import {
  ParticipantDetailsModalComponent,
  RESERVATION_DETAILS_QUERY_PARAM
} from '../../../../events/modals/participant-details-modal/participant-details-modal.component';

@Component({
  selector: 'app-upcoming-reservations-sub-list',
  templateUrl: './upcoming-reservations-sub-list.component.html',
  styleUrls: ['./upcoming-reservations-sub-list.component.css']
})
export class UpcomingReservationsSubListComponent implements OnInit, OnDestroy {

  public pageNumber = 1;
  @LocalStorage() public pageSizeCustomerUpcomingReservations;

  public filtersChangedSubject = new Subject<void>();

  public customerId: number;

  public reservationPartsList: ListResponse<CustomerReservationPart>;
  public loadingReservations = false;

  // Reservations auto-cancelled for nonpayment; hidden by default (PRO-1225).
  public showNonpaymentCancelledReservations = false;

  // The whole reservation history rather than only what is coming up; off by
  // default (PRO-1338).
  public showPastReservations = false;

  public customerNotFound = false;
  public serverError: number = null;

  // Make relevant library functions available to the template.
  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601TimeOnly = timeStringFromISO8601TimeOnly;
  public capitalizeFirstLetter = capitalizeFirstLetter;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  private customerParamsSubscription: Subscription;

  private reservationDetailsDeepLink: DetailsModalDeepLink<ParticipantDetailsModalComponent>;

  constructor(
    public accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private route: ActivatedRoute,
    private customerService: CustomerService,
    router: Router,
    modalService: NgbModal
  ) {
    this.reservationDetailsDeepLink = new DetailsModalDeepLink<ParticipantDetailsModalComponent>({
      router,
      route,
      modalService,
      queryParam: RESERVATION_DETAILS_QUERY_PARAM,
      component: ParticipantDetailsModalComponent,
      configure: (instance, reservationId) => { instance.reservationId = reservationId; }
    });
  }

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    if (!this.pageSizeCustomerUpcomingReservations) {
      this.pageSizeCustomerUpcomingReservations = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeCustomerUpcomingReservations = this.pageSizeCustomerUpcomingReservations;

    // Handle changes to the filters by reloading the list, subject to debounce.
    this.filtersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadReservations();
    });

    // The customer ID is on the grandparent (customer-detail) route.
    // Subscribed, not snapshotted: when only :id changes Angular reuses this
    // component without re-running ngOnInit.
    this.customerParamsSubscription =
      this.route.parent.parent.params.subscribe(routeParams => {
        this.customerId = parseInt(routeParams.id, 10);
        if (isNaN(this.customerId)) {
          this.customerNotFound = true;
          return;
        }
        this.customerNotFound = false;
        // Both gate the whole view; a stale error would cover the next
        // customer's data.
        this.serverError = null;
        this.pageNumber = 1;
        this.loadReservations();
      });

    this.reservationDetailsDeepLink.start();
  }

  ngOnDestroy() {
    if (this.customerParamsSubscription) {
      this.customerParamsSubscription.unsubscribe();
    }
    this.reservationDetailsDeepLink.stop();
  }

  public openReservationDetailsModal(reservationPart: CustomerReservationPart) {
    this.reservationDetailsDeepLink.open(reservationPart.reservation_id);
  }

  /**
   * Reload the list when one of the display toggles changes, resetting to the
   * first page.
   */
  public onFiltersChange() {
    this.pageNumber = 1;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.reservationPartsList) { return 1; }
    return Math.ceil(
      this.reservationPartsList.count /
      this.pageSizeCustomerUpcomingReservations
    );
  }

  public getResEventDetailsLink(reservationPart: CustomerReservationPart) {
    return getEventDetailsLink(
      reservationPart.start_datetime,
      reservationPart.end_datetime,
      reservationPart.start_local_date,
      reservationPart.event_part_id
    )
  }

  private loadReservations() {
    this.loadingReservations = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.customerService
      .getCustomerReservationParts(
        this.customerId,
        this.pageNumber,
        this.pageSizeCustomerUpcomingReservations,
        this.showNonpaymentCancelledReservations,
        this.showPastReservations
          ? CustomerReservationTimeScope.all
          : CustomerReservationTimeScope.upcoming
      )
      .then(results => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.reservationPartsList = results;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingReservations = false;
        }
      });
  }

}
